import RenderManager from "../rendering/render-manager";
import AssessmentManager from "../assessment/assessment-manager";
import Utils from "../utils";
import Network from "../miniviews/network";
import Splash from "../splash/splash";
import SlideManager from "../utils/slide-manager";
import { requestFullScreen } from "../utils/resize"
import addAnimation from "../slides/animation/addAnimation";
import ObjectManager from "../objects/object-manager";
import ensureArray from '../utils/ensure-array';
import AssetsBusy from "./assets-busy";
import Analytics from "../analytics";
import setAssetlibSlideVisibility from "./assetlib-visibility";
import retrieveMissingAnimationFileSizes from "./retrieve-animation-filesizes";

import * as DeserializeGeometry from "./deserialize-geometry";
import * as DeserializeTextures from "./deserialize-texture";
import * as DeserializeLights from "./asset-lights";
import * as DeserializeMaterial from "./deserialize-material";
import * as DeserializeObject from "./deserialize-object";
import * as DeserializeLivelabels from "./deserialize-livelabels";
import * as DeserializeAnimations from "./deserialize-animations";
import * as EnvLight from "../utils/env-light";

import AssetLibrary, { AssetQueue } from "./asset-library";
import { fire, listen } from "../utils/events";
import { isSlidePlaying } from "../slides/add-slide-controls";
import updateTonemapping from "../rendering/update-tone-mapping";
import findVisibleSlide from "../utils/find-visible-slide";
import fixJSON from "../utils/fix-json";
import TransformedBox3 from '../utils/transformedBox3';
import TransformedBox3Helper from '../utils/transformedBox3Helper';

import PivotPointMaterial from '../rendering/material-pivot-point.js';
import restoreFastHierarchyMap from "../utils/restore-fast-hierarchy";
import { recursiveFilter, filterBrokenAnimations, filterReferences } from "../utils/asset-filter";
import HierarchyBridge from "./hierarchy-bridge";
import { FAST_HIERARCHY_VERSION } from "../utils/fast-hierarchy-version";

const loaders = Object.freeze({
    texture: new THREE.TextureLoader(),
    image: new THREE.ImageLoader(),
    // HDRCube: new THREE.HDRCubeTextureLoader(),
    draco: window.injectDraco,
    GLTF: new THREE.GLTFLoader(),
    file: new THREE.FileLoader(),
    binary: new THREE.FileLoader()
});

loaders.binary.setMimeType("application/octet-stream");
loaders.binary.setResponseType("arraybuffer");

const isAutoloading = !Utils.queryParams.has("disable_autoload");
const isUsingDeferredLoading = !Utils.queryParams.has("no_deferred");
const isReimportingRefs = false;
const hierarchyWorker = new HierarchyBridge("/js/hierarchy.worker.js");

/**
 * Performs project and asset deserialization, imports assets and manages their references.
 * Manages loading queues.
 */
const AssetDeserializer = new class AssetDeserializer {
    constructor() {
        this._firstQueue = null;
        this._activeQueue = null;
        this._nextQueue = null;
        this._activeLODs = null;
        this.references = {};
    }

    get loaders() { return loaders; }
    get hierarchyWorker() { return hierarchyWorker; }

    checkLODs() {
        if (!Q3.isReady || !Q3.isFullyLoaded || (Q3.isTransitioning && isSlidePlaying()) || this._activeLODs) return;

        const lodQueueIndex = AssetLibrary.assetQueuesLOD.findIndex(q => q.slideIndex === SlideManager.activeSlide);

        if (lodQueueIndex === -1) return;

        const lodQueue = AssetLibrary.assetQueuesLOD[lodQueueIndex];
        const items = lodQueue.getItems(5);
        const assets = AssetLibrary;
        const data = Q3.data;
        const background = RenderManager.background;
        const mixer = Q3.mixer;
        const editMode = Q3.editMode;
        const livelabelManager = Q3.labels.live;
        const animationsWaiting = [];
        const scene = RenderManager.activeScene;

        if (items.length > 0) {
            this._activeLODs = Promise.all(items.map(item => new Promise(resolve => {
                item
                    .load({
                        assetMap: assets.assetMap,
                        loaders, assets,
                        data,
                        item: item.data,
                        background,
                        mixer,
                        editMode,
                        scene,
                        livelabelManager,
                        slide: SlideManager.activeSlide,
                        animationsWaiting
                    })
                    .then(data => data)
                    .then(() => resolve())
                    .catch(() => resolve());
            }))).then(() => this._activeLODs = null);
        } else {
            AssetLibrary.assetQueuesLOD.splice(lodQueueIndex, 1);
            AssetsBusy.checkSpinner();
        }
    }

    /**
     * Loads all geometries for given data.
     * @param {Object} props Parameters.
     * @param {Object} props.data Geometry data.
     * @param {boolean} props.isAsset Wheter or not we are loading an asset from library in order to force loading instantly.
     * @return {Promise}
     */
    _loadGeometries({ data, isAsset }) {
        const newGeometries = {};

        const promises = Promise.all(
            Object
                .keys(data.assets.geometries)
                .filter(key => DeserializeGeometry.isGeometryInUse({ uuid: key, data }))
                .map(key => {
                    const geometry = DeserializeGeometry.loadGeometry({
                        uuid: key,
                        data: data.assets.geometries[key],
                        loaders,
                        isAsset
                    });

                    newGeometries[key] = data.assets.geometries[key];

                    return geometry;
                })
        );

        data.assets.geometries = newGeometries;

        return promises;
    }

    /**
     * Loads all textures for given data.
     * Removes any textures that are not in use.
     * @param {Object} props Parameters.
     * @param {Object} props.data Texture data.
     * @param {boolean} props.isAsset Wheter or not we are loading an asset from library in order to force loading instantly.
     * @return {Promise}
     */
    _loadTextures({ data, isAsset }) {

        const newTextures = {};
        const promises = Promise.all(
            Object.keys(data.assets.textures)
                .filter(key => isAsset || Utils.queryParams.has("keep_unused_textures") || DeserializeTextures.isTextureInUse({ uuid: key, data }))
                .map(key => {
                    newTextures[key] = data.assets.textures[key];

                    return DeserializeTextures.loadTexture({
                        uuid: key, data: data.assets.textures[key],
                        materials: data.assets.materials,
                        slides: data.slides,
                        isAsset
                    });
                })
        );

        data.assets.textures = newTextures;

        return promises;

    }

    /**
     * Loads all lights for given data.
     * @param {Object} props Parameters.
     * @param {Object} props.data Light data.
     * @param {Object} props.browserCapabilities Browser capabilities, used to modify settings for weak devices.
     * @return {Promise}
     */
    _loadLights({ data, browserCapabilities }) {
        // const newLights = {};

        return new Promise((resolve, reject) => {
            Promise.all(
                Object.keys(data).map(key => {
                    const light = DeserializeLights.loadLight({
                        uuid: key,
                        data: data[key],
                        browserCapabilities
                    });

                    // newLights[key] = data[key];

                    return light;
                })
            ).then(
                lights => resolve(lights.filter(entity => !!entity.light)), //remove nulls from array (lights ignored by lq option)
                reject
            );
        });
    }

    /**
     * Loads env lights.
     * @return {Promise}
     */
    async _loadEnvLights() {
        const light = await EnvLight.initEnvLight();

        return { uuid: light.uuid, light };
    }

    /**
     * Loads all materials for given data.
     * Filters out any materials that are not in use.
     * @param {Object} props Parameters.
     * @param {Object} props.data Material data.
     * @param {Object} props.textures Texture list for applying material maps.
     * @param {Object} props.envMap Environment map used for PBR lightning.
     * @return {Promise}
     */
    _loadMaterials({ data, textures, envMap, envMapCubeRes }) {
        const newMaterials = {};

        const promises = Promise.all(
            Object.keys(data.assets.materials)
                .filter(key => DeserializeMaterial.isMaterialInUse({ uuid: key, data }))
                .map(key => {
                    const material = DeserializeMaterial.loadMaterial({
                        uuid: key,
                        data: data.assets.materials[key],
                        textures,
                        envMap,
                        envMapCubeRes,
                    });

                    newMaterials[key] = data.assets.materials[key];

                    return material;
                })
        );

        data.assets.materials = newMaterials;

        return promises;
    }

    /**
     * Loads all objects for given data.
     * @param {Object} props Parameters.
     * @param {Object} props.data Object data.
     * @param {Object} props.geometries Geometry data to be used in meshes.
     * @param {Object} props.materials Material data to be used in meshes.
     * @param {Object[]} props.slides Slide data.
     * @return {Promise}
     */
    _loadObjects({ data, geometries, materials, slides = [] }) {
        const activeSlide = SlideManager.activeSlide;

        return Promise.all(Object
            .keys(data)
            .sort((uuidA, uuidB) => {
                const typeA = data[uuidA].type;
                const typeB = data[uuidB].type;

                return getObjectPriority(typeA) - getObjectPriority(typeB);
            })
            .map(key => DeserializeObject.loadObject({
                uuid: key,
                data: data[key],
                assetData: data,
                geometries,
                materials,
                visible: slides[activeSlide]
                    ? slides[activeSlide].animation[key]
                        ? slides[activeSlide].animation[key].visible !== false
                        : true
                    : true
            }))).then(objects => {
                objects
                    .filter(x => x)
                    .forEach(({ uuid, object, error }) => {
                        if (error) {
                            console.error("Model couldn't load because raisins:", error);

                            slides.forEach(slide => {
                                slide.animation[uuid].deleted = true;
                            });

                            return;
                        }

                        DeserializeObject.finalizeObject({
                            object,
                            slides,
                            objects,
                            uuid,
                            data,
                            materials,
                            activeSlide
                        });
                    });

                return objects.filter(x => x && !x.error);
            });
    }

    /**
     * Adds deserialized lights to the scene with any required final parameters applied.
     * @param {Object} props Parameters.
     * @param {Object} props.lights Light list.
     * @param {Object} props.objects Object list.
     * @param {Object} props.data Project data.
     * @param {Object[]} props.slides Slide data.
     */
    _addLights({ lights, objects, data, slides }) {
        Object.keys(lights).forEach(uuid => {
            DeserializeLights.finalizeLight({ uuid, light: lights[uuid], objects, data, slides });
        });
    }

    /**
     * Bind skeletons to meshes.
     * @param {Object} props Parameters.
     * @param {Object} props.data Project data.
     * @param {Object} props.objects Object list.
     */
    _bindSkeletons({ data, objects }) {
        Object.keys(objects).forEach(uuid => {
            DeserializeObject.bindSkeleton({ uuid, data, object: objects[uuid], objects });
        });
    }

    /**
     * Collects slide assets into appropriate queues.
     * @param {Object} props Parameters.
     * @param {Object} props.data Project data.
     * @param {String} props.key UUID of the object to be deserialized.
     * @param {AssetQueue} props.queue Asset queue for the slide.
     * @param {AssetQueue} props.queueLOD Asset queue for the slide.
     * @param {AssetLibrary} props.assets Assets.
     */
    _collectAssets({ data, key, queue, queueLOD, assets }) {
        const object = data.assets.objects[key];

        if (!object) { console.warn("Unknown object:", key); return; }

        if (object.geometry && object.type !== "NexusMesh") {
            const geoData = data.assets.geometries[object.geometry];

            if (geoData) {
                let isValid = true;

                switch (geoData.type) {
                    case "Sphere": isValid = false; break;
                    case "Cube": isValid = false; break;
                    case "Arrow": isValid = false; break;
                    case "Plane": isValid = false; break;
                    case "Ring": isValid = false; break;
                    case "IconHotspot": isValid = false; break;
                    default: isValid = true; break;
                }

                if (isValid) {
                    DeserializeGeometry.collectGeometry({
                        key: object.geometry,
                        data,
                        queue: queue.geometries,
                        object,
                        assets
                    });
                }

            } else {
                console.warn("Unknown geometry:", object.geometry);
            }
        }

        if (object.material) {
            for (let material of ensureArray(object.material)) {
                DeserializeTextures.collectTextures({
                    key: material,
                    data,
                    queue: queue.textures,
                    queueLOD: queueLOD.textures,
                    object,
                    objectUuid: key,
                    assets
                });
            }
        }

        /*
        if (object.animation) {
            DeserializeAnimations.collectAnimations({ key: object.animation.uuid, data, queue: queue.animations, object });
        }
        */
    }

    /**
     * Enqueues assets for project.
     * @param {Object} props Parameters.
     * @param {AssetQueue} props.queue Asset queue.
     * @param {boolean} props.isAsset Wheter or not this is an asset and should be loaded in it's entirity.
     */
    _enqueAssets({ data, queue, isAsset, dataQ3, assetsQ3 }) {
        DeserializeTextures.enqueueTextures({ data: data.assets, queue: queue.textures, queuePriority: queue.highPriority, isAsset });
        DeserializeAnimations.enqueAnimations({ data: data.assets, queue: queue.animations });
        DeserializeLivelabels.enqueLivelabels({ data, queue: queue.livelabels, dataQ3, assetsQ3, isAsset });
    }

    /**
     * Loads all items in the givenasset queue.
     * @param {Object} props Parameters.
     * @param {AssetQueue} props.queue Asset queue to load assets from.
     * @param {AssetLibrary} props.assets Asset library in which to put items.
     * @param {Object} props.data Project data.
     * @param {Object} props.background Slide background.
     * @param {Q3.AnimationMixer} props.mixer Animation mixer.
     * @param {boolean} props.editMode Wheter or not we're in edit mode.
     * @param {THREE.Scene} props.scene Active scene.
     * @param {Object} props.livelabelManager Live label manager.
     * @return {Object}
     */
    _loadQueue({
        queue, assets, data, background, mixer, editMode, scene, livelabelManager, rejectOnFail = false
    }) {
        const animationsWaiting = [];

        console.log("Started loading queue for slide:", queue.slideIndex + 1);

        let highPriorityResolve, geometryResolve;
        let highPriorityReject, geometryReject;

        let allSettled = false;
        let highPrioritySettled = false;
        let geometrySettled = false;

        const highPriorityLoaded = new Promise((resolve, reject) => {
            highPriorityResolve = resolve;
            highPriorityReject = reject;
        });

        const geometryLoaded = new Promise((resolve, reject) => {
            geometryResolve = resolve;
            geometryReject = reject;
        });

        const allLoaded = new Promise((resolve, reject) => {
            queue.sort();
            const all = [];
            let layersInActions = data.layers ? Object.values(data.layers.layers2D || {})
                .map(l => Object.values(l.objects)[0])
                .filter(o => o && o.action && o.action.length)
                .reduce((acc, obj) => acc.concat(obj.action.filter(a => a.userAction === "slide" && a.systemAction === 'rewind' && a.value && a.value.objectUuid)), [])
                .map(a => a.value.objectUuid) : [];
            if (queue.slideIndex !== -1 && !Q3.editMode) {
                Object.keys(data.slides[queue.slideIndex].layers2D).filter((uuid) => {
                    if (data.slides[queue.slideIndex].layers[uuid] === false) return false;

                    const l = data.layers.layers2D[uuid];

                    if (!l || !Object.values(l.objects)[0]) return false;

                    const type = Object.values(l.objects)[0].type;

                    return type === "picture" || type === "video" || type === "imageCarousel";
                }).forEach((uuid) => {
                    const obj = Object.values(data.layers.layers2D[uuid].objects)[0];
                    let webpSupport = Q3.browserCapabilities.supports.WebP;
                    all.push(new Promise((resolve) => {
                        if (obj.type === "imageCarousel") {
                            Promise.all(obj.images.map(img => new Promise(resolveOne => {
                                const tempImg = new Image();

                                tempImg.setAttribute('crossorigin', "anonymous");
                                tempImg.onload = () => {
                                    resolveOne();
                                };
                                let src = img.src.split("?")[0].replace(Q3.s3Url, "");
                                let ext = src.split(".").pop();
                                let webp = ext !== "svg" && ext !== "gif" && ext !== "webp" && webpSupport;
                                tempImg.onerror = () => resolveOne();
                                tempImg.src = `${Q3.s3Url}${src}${webp ? ".webp" : ""}`;
                            }))).then(resolve)
                        } else if (obj.type === "picture") {
                            const tempImg = new Image();

                            tempImg.setAttribute('crossorigin', "anonymous");
                            tempImg.onload = () => {
                                obj.ratio = tempImg.height / tempImg.width;
                                resolve();
                            };

                            const isWebp = obj.extension !== "svg" && obj.extension !== "gif" && obj.extension !== "webp" && webpSupport
                            tempImg.onerror = () => resolve();

                            const src = new URL(obj.src, Q3.s3Url);
                            const { pathname, search } = src;

                            tempImg.src = `${Q3.s3Url}${pathname}${isWebp ? ".webp" : ""}${search}`;
                        } else {
                            let isResolved = false;
                            let timeout = null;
                            const resolvePromise = () => {
                                clearTimeout(timeout);
                                isResolved = true;
                                resolve();
                            }
                            timeout = setTimeout(() => {
                                if (!isResolved) {
                                    console.log(`${obj.src} preload timed out`);
                                    resolvePromise();
                                }
                            }, 5000)
                            const tempVideo = document.createElement("video");
                            let src = obj.src;
                            let eventName = "canplay";
                            if (Q3.browserCapabilities.isSafari) {
                                src = src.replace(".webm", ".mp4");
                                eventName = "loadedmetadata";
                                if (!Q3.editMode && obj.hasAlpha) {
                                    src = src.replace(/.mp4$|.webm$/g, "_alpha.mp4");
                                }
                            }
                            if ((layersInActions.indexOf(uuid) === -1 && !obj.overlaySeek) || Q3.browserCapabilities.isSafari) {

                                tempVideo.setAttribute("preload", "metadata");
                                tempVideo.setAttribute("crossorigin", "anonymous");
                                tempVideo.setAttribute("src", `${Q3.s3Url}${src}`);

                                const canPlay = () => {
                                    tempVideo.removeEventListener(eventName, canPlay);
                                    Q3.videoPreload[Object.keys(data.layers.layers2D[uuid].objects)[0]] = tempVideo;
                                    if (!isResolved) resolvePromise();
                                };
                                const onFocus = () => {
                                    tempVideo.addEventListener(eventName, canPlay);
                                    window.removeEventListener("focus", onFocus)
                                }
                                if (document.hidden) window.addEventListener("focus", onFocus)
                                else tempVideo.addEventListener(eventName, canPlay);

                                tempVideo.addEventListener("error", () => { if (!isResolved) resolvePromise() });

                            } else { //full preload
                                var req = new XMLHttpRequest();
                                req.open('GET', `${Q3.s3Url}${src}`, true);
                                req.responseType = 'blob';
                                req.onload = function () {
                                    if (this.status === 200) {
                                        tempVideo.src = URL.createObjectURL(this.response);
                                        Q3.videoPreload[Object.keys(data.layers.layers2D[uuid].objects)[0]] = tempVideo;
                                        if (!isResolved) resolvePromise();
                                    }
                                }
                                req.onerror = function () {
                                    if (!isResolved) resolvePromise();
                                }
                                req.send();
                            }
                        }
                    }));
                });
            }

            let waitingForLast = false;
            (function loadNextItems(count) {
                const items = queue.getItems(count);
                if (items.length > 0) {
                    fire("asset_batch_loading", { queue });

                    items.forEach(item => {
                        fire("asset_loading", { item, queue });

                        // console.log(`Loading '${item.data.data.name}' of type: ${item.data.type}`);

                        all.push(item.load({
                            assetMap: assets.assetMap,
                            loaders, assets,
                            data,
                            item: item.data,
                            background,
                            mixer,
                            editMode,
                            scene,
                            livelabelManager,
                            slide: SlideManager.activeSlide,
                            animationsWaiting
                        }).then(data => {
                            fire("asset_finished", { item, queue, error: false });

                            if (!waitingForLast) {
                                fire("asset_batch_finished", { queue, items });
                                loadNextItems(5);
                            }

                            return data;
                        }).catch(reason => {
                            fire("asset_finished", { item, queue, error: reason });

                            console.error("Failed to load object:", item.data, "Reason:", reason);

                            if (rejectOnFail) {
                                if (!highPrioritySettled) {
                                    highPriorityReject(reason);
                                    highPrioritySettled = true;
                                }

                                if (!geometrySettled) {
                                    geometryReject(reason);
                                    geometrySettled = true;
                                }

                                if (!allSettled) {
                                    reject(reason);
                                    allSettled = true;
                                }
                            } else {
                                if (!waitingForLast) {
                                    fire("asset_batch_finished", { queue });
                                    loadNextItems(5);
                                }
                            }
                        }));
                    });
                } else if (!waitingForLast) {
                    waitingForLast = true;

                    console.log("No more items:", queue.slideIndex + 1);

                    Promise.all(all).then(() => {
                        console.log("Loaded queue for slide:", queue.slideIndex + 1);

                        animationsWaiting.forEach(fn => fn());

                        if (!highPrioritySettled && queue.highPriority.isEmpty()) {
                            highPriorityResolve();
                            highPrioritySettled = true;
                        }

                        if (!geometrySettled && queue.geometries.isEmpty()) {
                            geometryResolve();
                            geometrySettled = true;
                        }

                        if (!allSettled) {
                            resolve();
                            allSettled = true;
                        }

                        const idx = assets.assetQueues.indexOf(queue)
                        if (idx >= 0) assets.assetQueues.splice(idx, 1);
                    })
                }
            })(5);
        });

        AssetsBusy.register({
            type: "3d",
            slide: queue.slideIndex,
            promise: allLoaded
        });

        return { allLoaded, highPriorityLoaded, geometryLoaded };
    }

    /**
     * Imports asset from asset library.
     * @param {Object} props Parameters.
     * @param {Object} props.data Asset data to import.
     * @param {AssetLibrary} props.assets Active asset library.
     * @param {Object} props.browserCapabilities Browser capabilities.
     * @param {Object} props.activeData Project data.
     * @param {number} props.slide Active slide index.
     * @param {Object} props.layer Active layers.
     * @return {Promise}
     */
    async importAsset({ data, assets, browserCapabilities, activeData, slide, layers }) {
        const { assetId, asset, variation_id } = data;
        const assetData = fixJSON(asset.data);
        const fastHierarchyMap = {};

        await hierarchyWorker.collectFastHierarchyMap(assetData, fastHierarchyMap);

        recursiveFilter(assetData, fastHierarchyMap.root.children);
        filterAssetlibBrokenSlideObjects(assetData);
        filterBrokenAnimations(assetData);

        activeData.assets.references.push(assetId);

        assets.references[assetId] = Object.freeze({
            id: assetId,
            name: asset.name,
            createdAt: asset.createdAt,
            description: asset.description,
            seafile_link: asset.seafile_link,
            subtype: asset.subtype,
            thumbnail: asset.thumbnail,
            tour: asset.tour,
            type: asset.type,
            update: asset.update,
            updatedAt: asset.updatedAt
        });

        Object.keys(assetData.assets).filter(k => k !== "neverUpdate").forEach(key => {
            const assetType = assetData.assets[key];
            const isObject = key === "objects";

            Object.keys(assetType).forEach(key => {
                assetType[key].isReference = true;
                assetType[key].referenceId = assetId;

                if (isObject) assetType[key].parent_world = Network.currentWorldUuid;
            });
        });

        const AssetTypes = ['animations', 'geometries', 'textures', /*'lights',*/ 'materials', 'objects'];

        const oldAssetUuids = {};
        for (let assetType of AssetTypes) {
            const assets = activeData.assets[assetType] || {};
            oldAssetUuids[assetType] = new Set(Object.keys(assets));
        }

        try {
            if (assetData.assets.animations) {
                if (!Q3.data.assets.animations)
                    Q3.data.assets.animations = {};

                Object.assign(Q3.data.assets.animations, assetData.assets.animations);
            }

            const assetQueue = new AssetQueue();

            addAnimation();

            activeData.assets.skeletons = activeData.assets.skeletons || {};
            if (assetData.assets.skeletons) {
                merge(activeData.assets.skeletons, assetData.assets.skeletons);
            }

            const _geometries = await this._loadGeometries({ data: assetData, isAsset: true });
            const geometries = reduceArray(_geometries, "geometry");

            merge(assets.geometries, geometries);
            merge(activeData.assets.geometries, assetData.assets.geometries);

            console.log("Geometry ready.");

            const _textures = await this._loadTextures({ data: assetData, isAsset: true });
            const textures = reduceArray(_textures, "texture");

            merge(assets.textures, textures);
            merge(activeData.assets.textures, assetData.assets.textures);

            console.log("Textures ready.");

            // const _lights = await this._loadLights({ data: assetData.assets.lights || {}, browserCapabilities });
            // const lights = reduceArray(_lights, "light");

            // merge(assets.lights, lights);
            // merge(activeData.assets.lights, assetData.assets.lights);

            // console.log("Lights ready.");

            const envMap = assets.envLight;

            const _materials = await this._loadMaterials({ data: assetData, textures, envMap: envMap.activeMap, envMapCubeRes: envMap.resolution });
            const materials = reduceArray(_materials, "material");

            merge(assets.materials, materials);
            merge(activeData.assets.materials, assetData.assets.materials);

            console.log("Materials ready.");

            const slides = assetData.slide ? activeData.slides.map(() => assetData.slide) : [];
            const _objects = await this._loadObjects({ data: assetData.assets.objects, geometries, materials, slides });
            const objects = reduceArray(_objects, "object");

            merge(assets.objects, objects);
            merge(activeData.assets.objects, assetData.assets.objects);

            if (assetData.assets.skeletons) {
                if (!activeData.assets.skeletons) {
                    activeData.assets.skeletons = {};
                }

                merge(activeData.assets.skeletons, assetData.assets.skeletons);
            }

            Object
                .values(objects)
                .forEach(obj => {
                    const isRoot = !Q3.data.assets.objects[obj.uuid].parent;

                    if (!isRoot) return;

                    obj.up.applyEuler(obj.rotation);
                    Q3.data.assets.objects[obj.uuid].up = { x: obj.up.x, y: obj.up.y, z: obj.up.z };
                });

            DeserializeAnimations.populateAnimationData({ data: activeData, additionalData: assetData });

            let visibleInSlides = activeData.slides.map(() => false);
            visibleInSlides[slide] = true;
            setAssetlibSlideVisibility(activeData, assetData, visibleInSlides);

            const SingleValMaterialProperties = ['opacity', 'emissiveIntensity', 'envMapIntensity', 'repeat', 'transparent'];
            const ColorMaterialProperties = ['color', 'emissive'];

            for (let key in assetData.assets.objects) {
                const objectData = assetData.assets.objects[key];

                if (assetData.slide) {
                    let animData = assetData.slide.animation[key];
                    Q3.assets.objects[key].visible = animData.visible !== false && animData.deleted !== true;
                }

                let materialUuids = ensureArray(objectData.material);
                let materials = materialUuids.map(uuid => Q3.assets.materials[uuid]);
                let animData = activeData.slides[slide].animation[key];
                let empty = {};
                let slideMatAnims = activeData.slides[slide].materialAnimation || empty;
                let materialsAnimData = materialUuids.map(uuid => Object.assign({}, animData || empty, slideMatAnims[uuid] || empty));

                for (let [materialIndex, matAnimData] of materialsAnimData.entries()) {
                    let material = materials[materialIndex];

                    for (let name of SingleValMaterialProperties) {
                        if (name in matAnimData) {
                            material[name] = matAnimData[name];
                        }
                    }
                    for (let name of ColorMaterialProperties) {
                        if (name in matAnimData) {
                            material[name].set(Number(matAnimData[name]));
                        }
                    }
                }
            }

            const totalTriangles = _objects.reduce((sum, { object }) => sum + ObjectManager.countFaces(object), 0);
            if (totalTriangles >= ObjectManager.maxFaces) ObjectManager.warnOverflown();

            console.log("Objects ready.");

            // this._addLights({ lights, objects, data: assetData, slides: activeData.slides });

            this._bindSkeletons({ data: assetData, objects });

            this._enqueAssets({ data: assetData, queue: assetQueue, isAsset: true, dataQ3: activeData, assetsQ3: assets });

            await this._loadQueue({
                queue: assetQueue,
                assets,
                data: assetData,
                background: RenderManager.background,
                mixer: Q3.mixer,
                scene: RenderManager.activeScene,
                editMode: Q3.editMode,
                livelabelManager: Q3.labels.live
            }).allLoaded;

            let amp_variations = {};
            if (Array.isArray(Q3.data.setups.amp_variations)) {
                amp_variations = Q3.data.setups.amp_variations.reduce((acc, v) => { acc[v.asset_id] = v.variation_id; return acc }, {})
            } else {
                amp_variations = Q3.data.setups.amp_variations || {}
            }

            Q3.data.setups.amp_variations = amp_variations;
            if (!Q3.data.setups.amp_variations[assetId]) {
                Q3.data.setups.amp_variations[assetId] = variation_id;
            }

            fire("assetsLoaded");

            RenderManager.activeCamera.update = true;

            return 1;

        } catch (error) {

            for (let objectUuid in assets.objects) {
                if (!oldAssetUuids.objects.has(objectUuid)) {
                    let object = assets.objects[objectUuid];
                    ObjectManager.deleteObjectFromTour(object);
                    object.parent.remove(object);
                }
            }

            for (let assetType of AssetTypes) {
                let oldUuids = oldAssetUuids[assetType];
                for (let uuid in activeData.assets[assetType]) {
                    if (!oldUuids.has(uuid)) {
                        delete activeData.assets[assetType][uuid];
                        delete assets[assetType][uuid];
                    }
                }
            }

            let refList = activeData.assets.references;
            let index = refList.indexOf(assetId);
            if (index !== -1) {
                refList.splice(index, 1);
            }

            delete assets.references[assetId];

            throw error;
        }
    }

    /**
     * Imports asset from asset library.
     * @param {Object} props Parameters.
     * @param {Object} props.assetData Asset data to load.
     * @param {Object} props.browserCapabilities Browser capabilities.
     * @param {Object} props.activeData Project data.
     * @param {number} props.slide Active slide index.
     * @return {Promise}
     */
    loadAssetFromData({ data, browserCapabilities, activeData, slide, animUuid }) {
        const assets = AssetLibrary;

        const assetData = { assets: data, liveLabels: {} };

        if (assetData.assets.animations) {
            if (!Q3.data.assets.animations)
                Q3.data.assets.animations = {};

            Object.assign(Q3.data.assets.animations, assetData.assets.animations);
        }

        const assetQueue = new AssetQueue();

        addAnimation();

        return new Promise(resolve => {
            this._loadGeometries({ data: assetData, isAsset: true }).then(_geometries => {
                const geometries = reduceArray(_geometries, "geometry");

                merge(assets.geometries, geometries);
                merge(activeData.assets.geometries, assetData.assets.geometries);

                console.log("Geometry ready.");

                this._loadTextures({ data: assetData, isAsset: true }).then(_textures => {
                    const textures = reduceArray(_textures, "texture");

                    merge(assets.textures, textures);
                    merge(activeData.assets.textures, assetData.assets.textures);

                    console.log("Textures ready.");

                    // const lightsData = assetData.assets.lights || {};
                    // this._loadLights({ data: lightsData, browserCapabilities }).then(_lights => {
                    //     const lights = reduceArray(_lights, "light");

                    //     merge(assets.lights, lights);
                    //     merge(activeData.assets.lights, lightsData);

                    //     console.log("Lights ready.");

                    const envMap = assets.envLight;

                    this._loadMaterials({ data: assetData, textures, envMap: envMap.activeMap, envMapCubeRes: envMap.resolution }).then(_materials => {
                        const materials = reduceArray(_materials, "material");

                        merge(assets.materials, materials);
                        merge(activeData.assets.materials, assetData.assets.materials);

                        console.log("Materials ready.");

                        this._loadObjects({ data: assetData.assets.objects, geometries, materials }).then(_objects => {
                            const objects = reduceArray(_objects, "object");

                            merge(assets.objects, objects);
                            merge(activeData.assets.objects, assetData.assets.objects);

                            if (assetData.assets.skeletons) {
                                if (!activeData.assets.skeletons) {
                                    activeData.assets.skeletons = {};
                                }

                                merge(activeData.assets.skeletons, assetData.assets.skeletons);
                            }

                            DeserializeAnimations.populateAnimationData({ data: activeData, additionalData: assetData });
                            if (animUuid) {
                                for (let objectUuid in assetData.assets.objects) {
                                    let objectData = assetData.assets.objects[objectUuid];
                                    if (!objectData.parent) {
                                        for (let slideData of Q3.data.slides) {
                                            let animData = slideData.animation[objectUuid];
                                            animData.animation = {
                                                timescale: 1,
                                                type: THREE.LoopRepeat,
                                                uuid: animUuid,
                                            };
                                        }
                                    }
                                }
                            }

                            let visibleInSlides = activeData.slides.map(() => false);
                            visibleInSlides[slide] = true;
                            setAssetlibSlideVisibility(activeData, assetData, visibleInSlides);

                            const totalTriangles = _objects.reduce((sum, { object }) => sum + ObjectManager.countFaces(object), 0);
                            if (totalTriangles >= ObjectManager.maxFaces) ObjectManager.warnOverflown();

                            console.log("Objects ready.");

                            // this._addLights({ lights, objects, data: assetData, slides: activeData.slides });

                            this._bindSkeletons({ data: assetData, objects });

                            this._enqueAssets({ data: assetData, queue: assetQueue, isAsset: true });

                            this._loadQueue({
                                queue: assetQueue,
                                assets,
                                data: assetData,
                                background: RenderManager.background,
                                mixer: Q3.mixer,
                                scene: RenderManager.activeScene,
                                editMode: Q3.editMode,
                                livelabelManager: Q3.labels.live
                            }).allLoaded.then(() => {
                                resolve(1);

                                RenderManager.activeCamera.update = true;
                            });

                        });
                    });
                    // });
                });
            })
        })
    }

    /**
     * Loads references.
     * @param {Object} props Parameters.
     * @param {Object} props.data Project data.
     * @param {boolean} props.isPreviewMode Is in preview mode.
     * @param {AssetLibrary} props.assets References
     * @return {Promise}
     */
    _loadReferences({ data, isPreviewMode, assets }) {
        if (!isReimportingRefs && !Q3.isAssetMode) return Promise.resolve([]);

        const existingReferences = Object.keys(data.assets).reduce((accum, key) => {
            const assetType = data.assets[key];

            if (key === "references") return accum;

            accum[key] = Object.keys(assetType).reduce((accum, key) => {
                if (assetType[key].isReference) {
                    accum[key] = assetType[key];
                    delete assetType[key];
                }

                return accum;
            }, {});

            return accum;
        }, {});

        return Promise.all(Object.values(data.assets.references).map(ref => {
            return new Promise((resolve, reject) => {
                const url = `${window.location.origin}${window.COOBO ? "/api" : ""}/assetlib/${ref}`;

                const success = asset => {
                    asset = JSON.parse(asset);
                    if (asset.error) {
                        console.error("Failed to import reference:", ref);
                        data.assets.references.splice(data.assets.references.indexOf(ref), 1);
                        resolve({ id: ref, object: null, error: asset.error });
                        return;
                    }

                    this.references[ref] = asset;
                    assets.references[ref] = Object.freeze({
                        id: asset.id,
                        name: asset.name,
                        createdAt: asset.createdAt,
                        description: asset.description,
                        seafile_link: asset.seafile_link,
                        subtype: asset.subtype,
                        thumbnail: asset.thumbnail,
                        tour: asset.tour,
                        type: asset.type,
                        update: asset.update,
                        updatedAt: asset.updatedAt
                    });

                    Object.keys(asset.data.assets).filter(k => k !== "neverUpdate").forEach(key => {
                        const assetType = asset.data.assets[key];
                        Object.keys(assetType).forEach(key => {
                            assetType[key].isReference = true;
                            assetType[key].referenceId = ref;
                        });
                    });

                    let assetLabels;
                    let labelUuids = [];
                    if (asset.data.liveLabels.version !== "2.1" && asset.data.s3Id) {
                        assetLabels = Object.keys(asset.data.liveLabels).reduce((acc, key) => {
                            acc[key] = { uuid: asset.data.liveLabels[key], s3Id: asset.data.s3Id };
                            return acc;
                        }, {});
                    } else {
                        assetLabels = asset.data.liveLabels || [];
                        let assetAnimData = asset.data.slide ? (asset.data.slide.animation || {}) : {}
                        labelUuids = assetLabels.labels.filter(l => l.label && l.label[0]).map(l => l.label[0].uuid)
                        data.slides.forEach(slide => {
                            labelUuids.forEach(key => {
                                if (!slide.animation[key] && assetAnimData[key]) {
                                    slide.animation[key] = JSON.parse(JSON.stringify(assetAnimData[key]));
                                }
                            })
                        })
                    }

                    if (!isPreviewMode) {
                        data.liveLabels = Object.assign({ version: "2.1", labels: [] }, data.liveLabels);
                        if ("labels" in assetLabels && assetLabels.labels instanceof Array)
                            data.liveLabels.labels.push(...assetLabels.labels);
                    }

                    const rootNodes = [];

                    Object.keys(asset.data.assets).filter(k => k !== "neverUpdate").forEach(key => {
                        const assetTypeLib = asset.data.assets[key];
                        const assetTypeRef = existingReferences[key] || {};
                        const assetType = data.assets[key] = data.assets[key] || {};
                        const isObject = key === "objects";
                        const isMaterial = key === "materials";

                        Object.keys(assetTypeLib).forEach(key => {
                            // If the tour already has this element, don't add it.
                            // Might happen when the user duplicates objects from
                            // imported asset. Since geometry data is retrieved by
                            // UUID, geometries can't be duplicated under a different
                            // UUID, but they must remain in the tour data for
                            // successful loading so the tour must treat it as its
                            // own data, not asset's.
                            if (assetType[key]) {
                                return;
                            }

                            if (isMaterial) assetType[key] = Object.assign({}, assetTypeLib[key], assetTypeRef[key]);
                            else assetType[key] = assetTypeLib[key];

                            if (assetTypeRef[key]) {
                                const refAsset = assetTypeRef[key];
                                const ref = assetType[key];
                                Object.keys(refAsset).forEach(key => {
                                    if (isObject) {
                                        // Keys not in this switch case are protected and will be overwritten by asset. Handle with care.
                                        switch (key) {
                                            case "parent_world":
                                            case "renderOrder":
                                            case "displayName":
                                                ref[key] = refAsset[key];
                                                break;
                                        }
                                    }
                                });
                            }

                            if (isObject) {
                                const object = assetTypeLib[key];

                                object.refId = ref;

                                if (Q3.editMode)
                                    ObjectManager.cacheRestoreTransform(key);

                                if (object.isHiddenRoot || !object.parent) { rootNodes.push(key); }

                                data.slides.forEach(slide => {
                                    if (!slide.animation[key]) {
                                        let animData;

                                        if (asset.data.slide) {
                                            animData = JSON.parse(JSON.stringify(asset.data.slide.animation[key]));
                                        } else {
                                            animData = {
                                                position: { x: object.position.x, y: object.position.y, z: object.position.z },
                                                rotation: { x: object.rotation.x, y: object.rotation.y, z: object.rotation.z },
                                                scale: { x: object.scale.x, y: object.scale.y, z: object.scale.z }
                                            };
                                        }

                                        slide.animation[key] = animData;
                                        if (data.setups.assetViewer) {
                                            slide.animation[key].highlight = "hover";
                                        }
                                    }
                                });
                            }

                            if (isMaterial && asset.data.slide && asset.data.slide.materialAnimation) {
                                for (let slideData of data.slides) {
                                    let animData = asset.data.slide.materialAnimation[key];
                                    if (animData) {
                                        animData = JSON.parse(JSON.stringify(animData));

                                        if (!slideData.materialAnimation) {
                                            slideData.materialAnimation = {};
                                        }

                                        if (!(key in slideData.materialAnimation)) {
                                            slideData.materialAnimation[key] = animData;
                                        }
                                    }
                                }
                            }
                        });
                    });

                    resolve({ id: ref, objects: rootNodes, labelUuids });
                }

                loaders.file.load(url, success, undefined, (err) => { console.log(err); });
            });
        })).then(loaders.file.setRequestHeader(undefined));
    }

    /**
     * Creates any assessments that are saved.
     * @param {Object} props Parameter.
     * @param {Object} props.data Project data.
     * @param {Object} props.objects Object list.
     */
    _createAssessments({ data, objects }) {
        Object.keys(data).forEach(key => {
            const ass = data[key];
            AssessmentManager.createAssessment(ass.type, {
                object: objects[key],
                matrix: new THREE.Matrix4().fromArray(ass.matrix)
            });
        })
    }

    /**
     * Deserializes the scene from project data.
     * @param {Object} props Parameters.
     * @param {Object} props.data Project data.
     * @param {Object} props.browserCapabilities Browser capabilities.
     * @param {number} props.curSlide Active slide index.
     * @param {boolean} props.isEditMode In editor mode.
     * @param {boolean} props.isPreviewMode In preview mode.
     * @param {HTMLElement} props.backgroundContainer Background container.
     * @return {Promise}
     */
    load({ data, browserCapabilities, curSlide, isEditMode, isPreviewMode, backgroundContainer, errorCallback }) {
        const loadTime = new Date() - window.performance.timing.navigationStart;

        console.log(`Time to assets: ${loadTime / 1000}`);

        const assets = AssetLibrary;
        const assetQueues = assets.assetQueues, assetQueuesLOD = assets.assetQueuesLOD;
        const hiddenQueues = [], hiddenQueuesLOD = [];
        const assetMap = assets.assetMap;

        if (!isUsingDeferredLoading || isEditMode) {
            assetQueues.push(new AssetQueue(0));
            assetQueuesLOD.push(new AssetQueue(0));
        }

        data.assets.references = data.assets.references || [];
        Q3.videoPreload = {};

        const filteredAnimationData = {};
        const fastHierarchyMap = {};

        return new Promise(resolve => {
            // Prevent errors for tours affected by object duplication bug where
            // duplicating an object from an imported asset breaks the tour when
            // the reference to the asset is removed (e.g. by updating the asset)
            const AssetTypes = ["objects", "geometries", "materials"];
            for (let assetType of AssetTypes) {
                for (let uuid in data.assets[assetType]) {
                    let assetData = data.assets[assetType][uuid];
                    if (!("isReference" in assetData) && "referenceId" in assetData) {
                        delete assetData.referenceId;
                    }
                }
            }

            this._loadReferences({ data, isPreviewMode, assets }).then(async references => {
                console.info(`%cDeserialized 'references' after ${(new Date() - window.performance.timing.navigationStart).toFixed(4)}ms`, "color: #ccccff; background: #333333;font-size:1.5rem");

                if (Q3.isAssetMode) {
                    const asset = Object.values(this.references)[0];

                    if (asset) document.title = asset.name + " - Coobo";
                }

                let startTime = performance.now();

                const hasHierarchy = data.hierarchy && typeof data.hierarchy[0] === "number" && data.hierarchy[0] === FAST_HIERARCHY_VERSION && Object.keys(data.hierarchy).length > 0;

                if (hasHierarchy) restoreFastHierarchyMap(data, fastHierarchyMap);
                else {
                    const dataJson = Q3.dataEvents ? JSON.parse(JSON.stringify(data)) : data;
                    await hierarchyWorker.collectFastHierarchyMap(dataJson, fastHierarchyMap);
                }

                console.info(`Collect fast hierarchy map: ${(performance.now() - startTime).toFixed(4)}ms`);
                startTime = performance.now();

                const labelUuids = references.reduce((acc, ref) => acc.concat(ref.labelUuids), []);

                filterReferences(data, fastHierarchyMap.root.children, data.assets.references);
                console.info(`Filter references: ${(performance.now() - startTime).toFixed(4)}ms`);
                startTime = performance.now();

                fastHierarchyMap.root.children.forEach(({ uuid, children }) => {
                    const object = data.assets.objects[uuid];
                    const refId = object.referenceId;

                    if (!refId || refId in this.references) return;

                    const objects = { [uuid]: object };

                    (function traverse(children) {
                        children.forEach(({ uuid, children }) => {
                            objects[uuid] = data.assets.objects[uuid];
                            traverse(children);
                        });
                    })(children);

                    this.references[refId] = { data: { assets: { objects } } };
                });

                recursiveFilter(data, fastHierarchyMap.root.children);
                console.info(`Recursive filter: ${(performance.now() - startTime).toFixed(4)}ms`);
                startTime = performance.now();

                filterBrokenSlideObjects(data, filteredAnimationData, labelUuids);
                console.info(`Filter broken objects: ${(performance.now() - startTime).toFixed(4)}ms`);
                startTime = performance.now();

                updateDataStructure(data);
                console.info(`Update data structure: ${(performance.now() - startTime).toFixed(4)}ms`);
                startTime = performance.now();

                filterBrokenAnimations(data);
                console.info(`Update broken animations: ${(performance.now() - startTime).toFixed(4)}ms`);

                const promiseGeometry = this._loadGeometries({ data });
                const promiseTexture = this._loadTextures({ data });
                const promiseEnvLights = this._loadEnvLights();

                console.info(`%cDeserialized 'filtering' after ${(new Date() - window.performance.timing.navigationStart).toFixed(4)}ms`, "color: #ccccff; background: #333333;font-size:1.5rem");

                promiseGeometry.then(_geometries => {
                    console.info(`%cDeserialized 'geometry' after ${(new Date() - window.performance.timing.navigationStart).toFixed(4)}ms`, "color: #ccccff; background: #333333;font-size:1.5rem");

                    const geometries = reduceArray(_geometries, "geometry");

                    Object.assign(assets.geometries, geometries);

                    console.log("Geometry ready.");


                    promiseTexture.then(_textures => {
                        const textures = reduceArray(_textures, "texture");

                        Object.assign(assets.textures, textures);

                        console.log("Textures ready.");

                        // this._loadLights({ data: data.assets.lights, browserCapabilities }).then(_lights => {
                        //     const lights = reduceArray(_lights, "light");

                        //     Object.assign(assets.lights, lights);

                        //     console.log("Lights ready.");

                        promiseEnvLights.then(_envLight => {
                            const envMap = assets.envLight;

                            assets.envLight.cubeMap = _envLight.light;

                            console.log("Environment light ready.");

                            this._loadMaterials({ data, textures, envMap: envMap.activeMap, envMapCubeRes: envMap.resolution }).then(_materials => {
                                const materials = reduceArray(_materials, "material");

                                Object.assign(assets.materials, materials);

                                console.log("Materials ready.");

                                this._loadObjects({ data: data.assets.objects, geometries, materials, slides: data.slides }).then(_objects => {
                                    const objects = reduceArray(_objects, "object");

                                    Object.assign(assets.objects, objects);

                                    console.log("Objects ready.");

                                    // this._addLights({ lights, objects, data, slides: data.slides });
                                    this._bindSkeletons({ data, objects });

                                    resolve(1);
                                });
                            });
                        });
                        // });
                    });
                });
            });
        }).then(result => {
            RenderManager.shadowMap.needsUpdate = true;
            const slide = data.slides[curSlide];

            if (!isEditMode) {
                backgroundContainer.parentElement.style.backgroundColor = slide.background.value.color.primary;
            }

            RenderManager.activeCamera.updateCamera();
            updateTonemapping();

            data.slides.forEach((slide, slideIndex) => {
                let assetQueue, assetQueueLOD;

                // if (!slide.visible && !isEditMode && !isPreviewMode) return;

                if (isUsingDeferredLoading && !isEditMode) {
                    assetQueue = new AssetQueue(slideIndex);
                    assetQueueLOD = new AssetQueue(slideIndex);

                    const isHidden = !slide.visible && !isPreviewMode;

                    (isHidden ? hiddenQueues : assetQueues).push(assetQueue);
                    (isHidden ? hiddenQueuesLOD : assetQueuesLOD).push(assetQueueLOD);
                } else {
                    assetQueue = assetQueues[0];
                    assetQueueLOD = assetQueuesLOD[0];
                }

                if (slide.background.type === "image" || slide.background.type === "texture" || slide.background.type === "panorama") {
                    const loadMobile = Q3.editMode || Q3.browserCapabilities.isMobile;

                    let addedMobile = false;

                    if (loadMobile) {
                        const backgroundTexture = data.assets.textures[slide.background.value.image.textureMobile];

                        if (backgroundTexture) {
                            const size = backgroundTexture.size;

                            DeserializeTextures.addBackgroundTextureToQueue({
                                uuid: slide.background.value.image.textureMobile,
                                texture: backgroundTexture,
                                queue: assetQueue.highPriority,
                                size
                            });

                            addedMobile = true;
                        }
                    }

                    const loadMain = Q3.editMode || !addedMobile;

                    if (loadMain) {
                        const backgroundTexture = data.assets.textures[slide.background.value.image.texture];

                        if (backgroundTexture) {
                            const size = backgroundTexture.size;

                            DeserializeTextures.addBackgroundTextureToQueue({
                                uuid: slide.background.value.image.texture,
                                texture: backgroundTexture,
                                queue: assetQueue.highPriority,
                                size
                            });
                        }
                    }
                }

                if (slide.controls.interactionType && slide.controls.asset) {
                    const animationUuid = slide.controls.asset;
                    // const animationData = data.assets.animations[animationUuid];

                    DeserializeAnimations.collectAnimations({
                        key: animationUuid,
                        data, queue: assetQueue.animations
                    });
                }

                if (slide.envLight) {
                    EnvLight.collectEnvLight({
                        path: slide.envLight,
                        queue: assetQueue.envlight
                    });
                }

                //deserialize animations started on slide enter
                Object.keys(slide.animation).forEach(key => {
                    const animation = slide.animation[key];

                    if (!animation) {
                        console.warn("Something broke the per slide settings.");
                        return;
                    }

                    const is3dVisible = slide.layers.Renderer;
                    const inSlide = isEditMode || !isUsingDeferredLoading || isInSlide(data, fastHierarchyMap, slideIndex, key) && is3dVisible;


                    if (!inSlide) return;


                    this._collectAssets({ data, key, queue: assetQueue, queueLOD: assetQueueLOD, assetMap, assets });

                    const objectAnimation = "animation" in animation
                        ? animation.animation
                        : data.assets.objects[key] && "animation" in data.assets.objects[key]
                            ? data.assets.objects[key].animation
                            : null;

                    if (objectAnimation && objectAnimation.uuid && !objectAnimation.isPreset) {
                        DeserializeAnimations.collectAnimations({
                            key: objectAnimation.uuid,
                            data, queue: assetQueue.animations
                        });
                    }

                    if (animation.action) {
                        for (let action of animation.action) {
                            const animationUuid = action && action.value && action.value.animationUuid;

                            if (animationUuid) {
                                DeserializeAnimations.collectAnimations({
                                    key: animationUuid,
                                    data,
                                    queue: assetQueue.animations
                                });
                            }
                        }
                    }
                });

                //deserialize animations enabled from layer 2d
                Object.keys(slide.layers2D).forEach(key => {
                    const layer2d = Q3.data.layers.layers2D[key];

                    if (layer2d) {
                        Object.keys(layer2d.objects).forEach(objUuid => {
                            const obj = layer2d.objects[objUuid];

                            if (obj.action) {
                                for (let j = 0; j < obj.action.length; j++) {
                                    const action = obj.action[j];

                                    if (action && action.value && action.value.animationUuid) {
                                        DeserializeAnimations.collectAnimations({
                                            key: action.value.animationUuid,
                                            data,
                                            queue: assetQueue.animations
                                        });
                                    }
                                }
                            }
                        });
                    }
                });
            });


            if (!isPreviewMode) {
                if (!Q3.isAssetMode) {
                    DeserializeLivelabels.enqueLivelabels({
                        data,
                        queue: assetQueues[0].livelabels,
                        filteredAnimationData
                    });
                }
            } else {
                DeserializeLivelabels.deserializeLivelabels({
                    assets,
                    data,
                    labels: data.liveLabels || [],
                    livelabelManager: Q3.labels.live,
                    filteredAnimationData
                });
            }

            fire("assets_queue_ready");

            assetQueues.push(...hiddenQueues);
            assetQueuesLOD.push(...hiddenQueuesLOD);
            this._loadFirstQueue(slide, assetQueues, assetQueuesLOD, assets, data, errorCallback);

            if (isEditMode) retrieveMissingAnimationFileSizes(Q3.data);

            return result;
        });
    }

    calculateQueueElements() {
        return [AssetLibrary.assetQueues, AssetLibrary.assetQueuesLOD].reduce((accum, queues) => {
            return accum + queues.reduce((accum, library) => accum + library.calculateSize(), 0);
        }, 0);
    }

    _prepNextQueue(queue, assets, data) {
        return this._loadQueue({
            queue,
            assets,
            data,
            background: RenderManager.background,
            mixer: Q3.mixer,
            scene: RenderManager.activeScene,
            editMode: Q3.editMode,
            livelabelManager: Q3.labels.live,
            //rejectOnFail: !Q3.editMode && !Utils.queryParams.has("ignore_load_errors"),
            rejectOnFail: false,
        });
    }

    _loadFirstQueue(slide, assetQueues, assetQueuesLOD, assets, data, errorCallback) {
        const loadingTimes = {};
        const slideIndex = findVisibleSlide();

        let customCursors = new Set(data.slides.reduce((acc, s) => acc.concat(Object.values(s.animation).filter(a => a && a.customCursor).map(a => a.customCursor)), []));
        let cursorPreload = document.createElement("div");
        document.body.appendChild(cursorPreload)
        customCursors.forEach(url => {
            let tempEl = document.createElement("div")
            tempEl.style.cursor = `url(${Q3.s3Url}${url}), auto`
            cursorPreload.appendChild(tempEl);
        });

        listen("asset_finished", function ({ item }) {
            loadingTimes[item.data.type].lastTime = performance.now();
        });

        listen("asset_loading", function ({ item }) {
            if (item.data.type in loadingTimes) return;

            const startTime = performance.now();
            loadingTimes[item.data.type] = { startTime: startTime, lastTime: startTime };
        });

        /**
         * @type {AssetQueue}
         */
        const firstQueue = assetQueues.find(a => a.slideIndex === slideIndex) || assetQueues[0];

        assetQueues.splice(assetQueues.indexOf(firstQueue), 1);

        this._firstQueue = this._activeQueue = firstQueue;

        const { allLoaded, highPriorityLoaded, geometryLoaded } = this._prepNextQueue(firstQueue, assets, data);

        const progressBar = $("#progressBar");
        // const parentWidth = progressBar.parent().width();
        // const startWidth = progressBar.width();
        const startSize = firstQueue.calculateSize();
        progressBar.css("transition", "width 0.5s linear");

        Splash.setProgressText(startSize > 0 ? `0/${startSize}` : "Finalizing...");



        listen("asset_batch_finished", function ({ items }) {
            const currentSize = firstQueue.calculateSize();

            if (Q3.editMode) {
                Splash.update(1 - currentSize / startSize);
            }

            if (currentSize > 0) {
                if (startSize > 0)
                    Splash.setProgressText(`${startSize - currentSize}/${startSize}`);
            } else {
                Splash.setProgressText("Finalizing...");
            }

        });

        geometryLoaded.then(() => {
            if (SlideManager.activeSlide !== slideIndex)
                SlideManager.setSlide(slideIndex);

            if (Q3.editMode || Utils.queryParams.has("autostart") || Q3.teachMode) {
                Splash.remove(false);
                // fire("commit_slide");
            } else if (-1 < window.location.pathname.indexOf('view_asset')) {
                Splash.remove(false);
                fire("commit_slide");
            } else {
                Splash.setProgressText("Ready.");

                progressBar.width("100%").addClass("loaded");

                $("#start-tour-btn .play-image").addClass("loaded").css("opacity", 1);
                function removeSplash() {
                    if (Q3.browserCapabilities.isMobile && window.COOBO)
                        requestFullScreen();

                    Splash.remove(false);
                    window.removeEventListener("keyup", removeSplashWithEnter);

                    // fire("commit_slide", { noAnimation: true });
                }

                function removeSplashWithEnter(e) {
                    if (e.code === "Enter")
                        removeSplash();
                }

                $("#start-tour-btn")
                    .css("pointer-events", "auto")
                    .on("click touchup", () => removeSplash());

                window.addEventListener("keyup", removeSplashWithEnter);
            }

            console.log(window.performance);

            const loadTime = new Date() - window.performance.timing.navigationStart;

            console.log(`Time to interaction: ${loadTime / 1000}`);

            Analytics.timeToInteraction = loadTime + "ms";

            fire("assetsLoaded");
            fire("labelsLoaded");

            clearInterval(window.intervalBar);
            delete window.counterIntervalBar

            RenderManager.activeCamera.updateCamera();

            const getViewWidth = () => Q3.RenderManager.getSize().x;

            if (Q3.isAssetMode) {
                Q3.boundingBoxHelpers = [];
                Object
                    .values(assets.objects)
                    .forEach(object => {
                        const isRoot = !Q3.data.assets.objects[object.uuid].parent;

                        if (!isRoot) return;

                        const trBox3 = new TransformedBox3();
                        trBox3.setFromObject(object);
                        const boxSize = trBox3.box.min.distanceTo(trBox3.box.max);

                        const hGeo = new THREE.BufferGeometry();
                        hGeo.addAttribute('position', new THREE.BufferAttribute(new Float32Array([0, 0, 0]), 3));
                        const hMat = new PivotPointMaterial({ size: 20, getViewWidth, sizeAttenuation: false });
                        const hObj = new THREE.Points(hGeo, hMat);
                        hObj.visible = false;
                        object.add(hObj);

                        const color = 0xFFA500;
                        // const color = Math.round(Math.random() * 0xffffff);
                        Q3.boundingBoxHelpers.push(hObj);
                        const helper = new TransformedBox3Helper(object, color);
                        const helperMat = new THREE.LineBasicMaterial({ color, depthTest: false, depthWrite: false, transparent: true });
                        helper.material = helperMat;
                        helper.isUpdatable = true;
                        helper.visible = false;
                        RenderManager.activeScene.add(helper);
                        Q3.boundingBoxHelpers.push(helper);
                    });
            }

            console.log("Everything is ready.");
            fire("loaded");
        });

        geometryLoaded.catch(error => {
            if (errorCallback) {
                errorCallback(error);
            }
        });

        highPriorityLoaded.then(() => {
            const bgType = slide.background.type;
            let bgValue;

            switch (bgType) {
                case "panorama":
                case "environment":
                case "image":
                case "texture":
                    bgValue = (Q3.editMode ? Q3.mobileView : Q3.browserCapabilities.isMobile)
                        && slide.background.value.image.textureMobile
                        || slide.background.value.image.texture;
                    break;
                case "color": bgValue = slide.background.value.color.primary; break;
                case "video": bgValue = slide.background.value.video; break;
            }

            RenderManager.background.setBackground(Object.assign(
                {}, slide.background, { tweenTime: 0 }
            ));

            SlideManager.setSlide(SlideManager.activeSlide, { force: true, noAnimation: true, start: true });
        });

        allLoaded.then(() => {
            if (Q3.isAssetMode) {
                DeserializeLivelabels.enqueLivelabels({
                    data,
                    queue: firstQueue.livelabels,
                    isAsset: true,
                    dataQ3: data,
                    assetsQ3: assets
                });
            }
            fire("assetsLoaded");

            Object.keys(loadingTimes).forEach(key => {
                const { lastTime, startTime } = loadingTimes[key];
                const dt = lastTime - startTime;

                console.info(`%cFinished '${key}' after ${dt.toFixed(4)}ms`, "color: #ccccff; background: #333333;font-size:1.5rem");
            });

            this._firstQueue = null;
        });

        this._onQueueLoad(allLoaded, firstQueue, assetQueues, assetQueuesLOD, assets, data);
    }

    /**
     * @param {AssetQueue[]} assetQueues
     */
    _pickNextQueue(assetQueues, assetQueuesLOD, assets, data) {

        if (!isAutoloading && !this._nextQueue) {
            return;
        }

        const hasQueues = this._nextQueue || assetQueues.length > 0;

        if (!hasQueues) return;

        if (Q3.isTransitioning && isSlidePlaying()) {
            requestAnimationFrame(this._pickNextQueue.bind(this, assetQueues, assetQueuesLOD, assets, data));
            return;
        }

        function findNextSlide(queue) {
            return queue.slideIndex > SlideManager.activeSlide
                && SlideManager.getSlide(queue.slideIndex).visible !== false;
        }

        if (Q3.browserCapabilities.supports.autoload.enabled) {

            const queue = this._nextQueue || assetQueues.find(findNextSlide) || assetQueues.shift();

            this._activeQueue = queue;
            this._nextQueue = null;

            const { allLoaded } = this._prepNextQueue(queue, assets, data);

            this._onQueueLoad(allLoaded, queue, assetQueues, assetQueuesLOD, assets, data);
        } else {
            const expectedSlide = Q3.SlideManager.slides.findIndex((slide, index) => slide.visible && Q3.SlideManager.activeSlide < index);

            if (expectedSlide === -1) return;

            const actualQueue = this._nextQueue || assetQueues.find(findNextSlide);

            if (expectedSlide === actualQueue.slideIndex) {
                const queue = actualQueue;

                this._activeQueue = queue;
                this._nextQueue = null;

                const { allLoaded } = this._prepNextQueue(queue, assets, data);

                this._onQueueLoad(allLoaded, queue, assetQueues, assetQueuesLOD, assets, data);
            } else {
                console.log("actualQueue:", actualQueue.slideIndex + 1, "| expectedSlide:", expectedSlide + 1)
            }
        }
    }

    _onQueueLoad(allLoaded, queue, assetQueues, assetQueuesLOD, assets, data) {
        allLoaded.then(() => {
            this._activeQueue = null;

            console.log("Queue loaded:", queue.slideIndex + 1);

            if (SlideManager.activeSlide === queue.slideIndex) {
                const slide = data.slides[queue.slideIndex];

                RenderManager.background.setBackground(Object.assign(
                    {}, slide.background, { tweenTime: 0 }
                ));
            }

            this._pickNextQueue(assetQueues, assetQueuesLOD, assets, data);
        });
    }

    /**
     * Load slide queue.
     * @param {Object} props Parameters.
     * @param {number} props.slide Slide index.
     * @param {AssetLibrary} props.assets Asset library.
     * @param {Object} props.data Project data.
     */
    loadSlideQueue({ slide, assets, data }) {
        const assetQueues = assets.assetQueues;
        const assetQueuesLOD = assets.assetQueuesLOD;

        const queue = assetQueues.find(q => slide === -1 && !this._activeQueue || q.slideIndex === slide);

        if (!queue) return;

        if (this._activeQueue)
            this._nextQueue = queue;
        else {
            this._activeQueue = queue;

            const { allLoaded } = this._prepNextQueue(queue, assets, data);

            this._onQueueLoad(allLoaded, queue, assetQueues, assetQueuesLOD, assets, data);
        }
    }
}

export default AssetDeserializer;
export { AssetDeserializer };

/**
 * Utility for merging objects.
 * @param {Object} original Object in which to merge to.
 * @param {Object} additional Object from which to get additinal properties.
 */
function merge(original, additional) {
    Object.keys(additional).forEach(key => {
        original[key] = additional[key];
    });
}

/**
 * Turns an array of elements containing some property into hashmap using that propert as key.
 * @param {Object} data Array to map.
 * @param {string} key Property to use as key.
 */
function reduceArray(data, key) {
    return data.reduce((accum, params) => {
        accum[params.uuid] = params[key];

        return accum;
    }, {});
}


function updateDataStructure(data) {
    Object.keys(data.assets.objects).forEach(key => {
        const object = data.assets.objects[key];

        if (object.subType === "Spline") {
            console.log(`Outdated data structure for object '${key}'`);
            object.type = "Spline";
            delete object.subType;
        }
    });
}

function filterBrokenSlideObjects(data, filteredData, labelUuids) {
    data.slides.forEach((slide, index) => {
        Object.keys(slide.animation).forEach(key => {
            if (!(data.assets.objects[key] /*|| data.assets.lights[key]*/) && slide.animation[key] && labelUuids.indexOf(key) === -1) {

                filteredData[key] = filteredData[key] || {};
                filteredData[key][index] = slide.animation[key];

                delete slide.animation[key];
            }
        });
    });
}

function filterAssetlibBrokenSlideObjects(data) {
    const slide = data.slide;

    if (slide && slide.animation) {
        Object.keys(slide.animation).forEach(key => {
            if (!data.assets.objects[key] && slide.animation[key]) {
                delete slide.animation[key];
            }
        });
    }
}

function isInSlide(data, fastMap, slideIndex, key) {
    return data.assets.objects[key] && fastMap[key]
        ? fastMap[key].slides[slideIndex].inSlide
        : false;
}

function getObjectPriority(type) {
    switch (type) {
        case "Molecule":
        case "ProceduralObject": return 2;
        case "MoleculeMesh":
        case "ProceduralMesh": return 1;
        default: return 0;
    }
}
